import React, { FC, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import toast from "react-hot-toast";
import { addToCart } from "store/slices/cartSlice";
import { Link } from "react-router-dom";
import NotifyAddTocart from "../../shared/NotifyAddTocart";
import NcInputNumber from "shared/NcInputNumber";
import { ICartItem } from "store/types/cartTypes";
import { ShoppingCart } from "lucide-react";
import { filterImageByAttributes } from "utils/AttributeHandlers";
import { IProductCard } from "Interfaces/interface.product";
import ToTitleCase from "utils/ToTitleCase";
import { ITEM_TYPES } from "utils/itemTypeHelpers";

const ProductCard: FC<IProductCard> = ({ data, dispatch, showViewDetails = false }) => {

  const [quantity, setQuantity] = useState(25);
  const [isAdding, setIsAdding] = useState(false);
  const [isQuantityChanging, setIsQuantityChanging] = useState(false);
  const [isQuantityInputFocused, setIsQuantityInputFocused] = useState(false);
  const productName = data.name || "";
  const productPrice = parseFloat(data.variants?.[0]?.price || "0");
  const variantId = data.variants?.[0]?.id || "";
  const productImage = data.images;

  // ... (notifyAddTocart, handleAddToCart, displayImage same as before)
  const notifyAddTocart = () => {
    const image = displayImage();
    toast.custom(
      (t) => (
        <NotifyAddTocart
          productImage={image}
          qualitySelected={quantity}
          show={t.visible}
          variantActive={0}
          price={productPrice}
          name={productName}
          onClose={() => toast.dismiss(t.id)}
        />
      ),
      { position: "top-right", id: "nc-product-notify", duration: 3000 }
    );
  };

  const handleAddToCart = async () => {
    const cartItem: ICartItem = {
      id: data.id,
      productId: data.id,
      variantId,
      quantity,
      variants: data.variants,
      price: productPrice,
      name: productName,
      images: productImage,
      identification: data.identification,
      category: data.category?.name || "",
      brand: data.brand?.name || "",
      description: data.description || "",
      itemType: ITEM_TYPES.PRODUCTS,
    };

    try {
      setIsAdding(true);
      const result = await dispatch(addToCart(cartItem));
      if (addToCart.fulfilled.match(result)) {
        notifyAddTocart();
      } else if (addToCart.rejected.match(result)) {
        toast.error(result.payload || "Failed to add to collection");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setIsAdding(false);
    }
  };
  const displayImage = () => {
    const attributes = data.variants?.[0]?.attributes;

    if (!attributes) {
      return data.images?.[0]?.images?.[0] || null;
    }

    const matchingImageObject = filterImageByAttributes(data, attributes);

    if (
      matchingImageObject?.images &&
      Array.isArray(matchingImageObject.images) &&
      matchingImageObject.images.length > 0
    ) {
      return matchingImageObject.images[0];
    }

    return data.images?.[0]?.images?.[0] || null;
  };

  return (
    <div className="bg-white hover:shadow-xl transition-shadow duration-300 overflow-hidden rounded-lg flex flex-col h-full w-full">
      <div className="relative w-full h-32 sm:h-40 md:h-48 overflow-hidden flex items-center justify-center flex-shrink-0">
        <Link to={`/product/${data.id}`} className="block w-full h-full">
          {productImage ? (
            <img
              src={displayImage() ?? ""}
              alt={data.name}
              className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`w-full h-full flex items-center justify-center bg-gray-200 text-gray-400 ${productImage ? "hidden" : ""
              }`}
          >
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </Link>
      </div>

      <div className="p-1 space-y-1 md:space-y-2 flex flex-col flex-1">
        <h3 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
          <Link
            to={`/product/${data.id}`}
            className="hover:text-blue-600 transition-colors"
            title={data.name}
          >
            <ToTitleCase text={`${data.name}`} />
          </Link>
        </h3>

        <p className="text-sm sm:text-base font-semibold text-gray-900">
          {`₹${productPrice.toFixed(2)}`}
        </p>

        <div className="mt-auto pt-2">
          {!showViewDetails && (
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm text-gray-600 flex-shrink-0">Qty:</span>
              <NcInputNumber
                min={25}
                max={50000}
                defaultValue={quantity}
                size="sm"
                disabled={isAdding}
                widthClass="w-[110px] sm:w-[130px]"
                onChange={(val) => setQuantity(val)}
                onQuantityChanging={setIsQuantityChanging}
                onInputFocusChange={setIsQuantityInputFocused}
              />
            </div>
          )}

          {showViewDetails ? (
            <Link to={`/product/${data.id}`}>
              <ButtonPrimary className="w-full text-xs sm:text-sm md:!py-2 !px-3 !shadow-none !bg-indigo-600 hover:!bg-indigo-700">
                View Details
              </ButtonPrimary>
            </Link>
          ) : (
            <ButtonPrimary
              className="w-full text-xs sm:text-sm py-1 md:!py-2 !px-2 !shadow-none"
              onClick={handleAddToCart}
              disabled={isAdding || isQuantityChanging || isQuantityInputFocused}
              loading={isAdding}
            >
              <span>
                {!isAdding && (
                  <span className="flex items-center justify-center gap-1">
                    <ShoppingCart className="text-white w-5 h-5" /> Add To Collection
                  </span>
                )}
              </span>
            </ButtonPrimary>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
